import { memo, useEffect } from 'react';
import { useSRZId } from '@/hooks';
import { Mainboard } from '@/pages/HomePage/OrganizationPage/Mainboard';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { Navigate, useLocation } from 'react-router-dom';
import { PopupContextProvider, DetailsPanelProvider } from '@/contexts';
import { PageLayoutMain, PageLayoutRoot } from '@/components/PageLayout';
import { selectDirty } from '@/components/studio/ScopingEditor/reducer';
import { useSelector } from 'react-redux';
import DashboardHeader from '@/pages/DashboardHeader';
import { SettingsPage } from '@/pages/Settings';
import ConnectPage from '@/pages/Connect';
import { useSpaceAccess } from '@/contexts/SpaceAccessContext';
import { NoSpacesAvailable } from '@/components/NoSpacesAvailable';
import { useFeatureGate } from '@statsig/react-bindings';

export const OrganizationPage = () => {
  const { spaceId, roomId, zoneId, buildingId } = useSRZId();
  const location = useLocation();
  // Default-landing redirect lives in `DefaultSpaceRedirector` (mounted by
  // `<Sidebar />` below). It walks the cached site tree, honours
  // `?siteId=…` as a preference, picks a leaf when the user only has
  // room/zone access, and replaces history. A second async resolver here
  // would race it (and silently overwrite its choices with a push), so
  // this file only owns the "do we have anything to render?" decision.
  const { isLoading: isAccessLoading, hasAnyAccess } = useSpaceAccess();

  const isDirty = useSelector(selectDirty);
  const { value: connectPageEnabled } = useFeatureGate('connect_page');

  useEffect(() => {
    const onbeforeunload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    if (isDirty) {
      window.addEventListener('beforeunload', onbeforeunload);
    }

    return () => window.removeEventListener('beforeunload', onbeforeunload);
  }, [isDirty]);

  const isDevicesRoute = location.pathname.startsWith('/devices');
  const hasNoSpaceAccess = !isAccessLoading && !hasAnyAccess();
  const showMainboard = !!(spaceId || roomId || zoneId || buildingId || isDevicesRoute);

  return (
    <PopupContextProvider>
      <DetailsPanelProvider>
        <DashboardHeader />
        <PageLayoutRoot>
          <Sidebar />
          {location.pathname.startsWith('/settings') ? (
            <SettingsPage />
          ) : location.pathname.startsWith('/connect') ? (
            connectPageEnabled ? (
              <ConnectPage />
            ) : (
              <Navigate to="/" replace />
            )
          ) : (
            <PageLayoutMain>
              <Header />
              {showMainboard && <Mainboard isRoom={!!roomId} isZone={!!zoneId} />}
              {hasNoSpaceAccess && !showMainboard && <NoSpacesAvailable />}
            </PageLayoutMain>
          )}
        </PageLayoutRoot>
      </DetailsPanelProvider>
    </PopupContextProvider>
  );
};

export default memo(OrganizationPage);
